import React, { FC, useMemo } from 'react';
import AntdForm from 'antd/es/form';
import Form from 'components/Form/Form';
import { FormItem } from 'components/Form/FormItem';
import { Input } from 'components/Input';
import Select from 'antd/lib/select';
import Button from 'components/Button/Button';
import Alert from 'components/Alert/Alert';
import { FormInstance } from 'antd/es/form/Form';
import { useGetUserGroupsQuery } from 'redux/apiSlices/userGroups.slice';
import { useGetMemberRolesQuery } from 'redux/apiSlices/members.slices';
import {
  ArrowLeft,
  ArrowRight,
  PlusIcon,
  TrashIcon,
} from 'pages/public/authFlow/icons';

type TInviteStepProps = {
  form: FormInstance<any>;
  onBack: () => void;
  onSkip: () => void;
  onSend: () => void;
  sendLoading?: boolean;
  error?: string;
};

/** Onboarding step 2 — Invite your members (ZEST-738 redesign). */
const InviteStep: FC<TInviteStepProps> = ({
  form,
  onBack,
  onSkip,
  onSend,
  sendLoading,
  error,
}) => {
  const { data: userGroups } = useGetUserGroupsQuery({
    page: 0,
    pageSize: 2000,
    search: '',
    sort: 'name,ASC',
  });
  const { data: roles = [] } = useGetMemberRolesQuery();

  const groupOptions = useMemo(
    () =>
      (userGroups?.content || []).map(g => ({ value: g.id, label: g.name })),
    [userGroups],
  );
  const roleOptions = useMemo(
    () => roles.map(role => ({ value: role, label: role })),
    [roles],
  );

  return (
    <>
      <div className="onb__header">
        <h2 className="onb__title">Invite your members</h2>
        <p className="onb__subtitle">
          Get started by sending instant invitations directly to your core
          dispatch and technician teams.
        </p>
      </div>

      <Alert type="error" message={error} />

      <Form form={form} name="invite" onFinish={onSend}>
        <AntdForm.List
          name="invitation"
          initialValue={[
            { email: undefined, groupIds: undefined, role: undefined },
          ]}
        >
          {(fields, { add, remove }) => (
            <div className="onb__fields">
              {fields.map(field => (
                <div className="onb__row" key={field.key}>
                  <FormItem
                    name={[field.name, 'email']}
                    label="Email Address"
                    style={{ flex: '0 0 190px' }}
                    rules={[
                      { required: true, message: 'Required' },
                      { type: 'email', message: 'Invalid email' },
                    ]}
                  >
                    <Input placeholder="tech@company.com" />
                  </FormItem>
                  <FormItem
                    name={[field.name, 'groupIds']}
                    label="User Group"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select
                      mode="multiple"
                      maxTagCount="responsive"
                      placeholder="Select group"
                      options={groupOptions}
                    />
                  </FormItem>
                  <FormItem
                    name={[field.name, 'role']}
                    label="Role"
                    rules={[{ required: true, message: 'Required' }]}
                  >
                    <Select placeholder="Select role" options={roleOptions} />
                  </FormItem>
                  {fields.length > 1 && (
                    <button
                      type="button"
                      className="onb__remove"
                      aria-label="Remove user"
                      onClick={() => remove(field.name)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="onb__add"
                onClick={() => add({ email: undefined })}
              >
                <PlusIcon />
                Add new user
              </button>
            </div>
          )}
        </AntdForm.List>

        <div className="onb__footer">
          <button type="button" className="onb__text-btn" onClick={onBack}>
            <ArrowLeft />
            Back
          </button>
          <div className="onb__footer-right">
            <button
              type="button"
              className="onb__text-btn onb__text-btn--muted"
              onClick={onSkip}
            >
              Skip for now
            </button>
            <Button type="primary" htmlType="submit" loading={sendLoading}>
              Send Invites &amp; Launch&nbsp;
              <ArrowRight />
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default InviteStep;
